// src/components/CompetitionSystems/H2HModal/H2HModal.js
import React from "react";
import styles from "./H2HModal.module.css";
import CustomModal from "../../../UI/Modal/CustomModal";
import Spinner from "../../Spinner/Spinner";

/** pomocniki (porównywanie par jako zbiorów, niezależnie od kolejności) */
const asTeamSet = (u, p) => new Set([u, p].filter(Boolean));
const setsEqual = (a, b) => a.size === b.size && [...a].every((v) => b.has(v));

/** krótkie wyświetlanie imienia (Inicjał) i nazwiska */
const shortPerson = (first, last) => {
    const fi = first ? `${first.charAt(0)}` : "";
    return [last, fi].filter(Boolean).join(" ");
};

/** format drużyny z opcjonalnym partnerem */
const formatTeamLabel = (pFirst, pLast, partnerFirst, partnerLast) => {
    const base = shortPerson(pFirst, pLast);
    const partner = partnerLast || partnerFirst ? shortPerson(partnerFirst, partnerLast) : "";
    return partner ? `${base} / ${partner}` : base;
};

const H2HModal = ({ isOpen, onRequestClose, match, h2hData, isLoading }) => {
    const [isInfoOpen, setIsInfoOpen] = React.useState(false);
    const [modalMessage, setModalMessage] = React.useState("");


     const leftLabel = React.useMemo(() => (
           formatTeamLabel(
                 match?.participant1FirstName,
                 match?.participant1LastName,
                 match?.participant1PartnerFirstName,
                 match?.participant1PartnerLastName
               )
         ), [
           match?.participant1FirstName,
           match?.participant1LastName,
           match?.participant1PartnerFirstName,
           match?.participant1PartnerLastName
         ]);

         const rightLabel = React.useMemo(() => (
           formatTeamLabel(
                 match?.participant2FirstName,
                 match?.participant2LastName,
                 match?.participant2PartnerFirstName,
                 match?.participant2PartnerLastName
               )
         ), [
           match?.participant2FirstName,
           match?.participant2LastName,
           match?.participant2PartnerFirstName,
           match?.participant2PartnerLastName
         ]);

    /** strona zwycięzcy meczu (P1/P2) */
    const getWinnerSide = React.useCallback((m) => {
        const winnerPid = m.walkover ? m.walkoverWinnerId : m.participantWinnerId;
        if (!winnerPid) return null;
        if (winnerPid === m.participant1Id) return "P1";
        if (winnerPid === m.participant2Id) return "P2";
        return null;
    }, []);

    /** zbiory lewego/prawego zespołu wg NAGŁÓWKA (czyli bieżącego meczu) */
    const leftTeamSet = React.useMemo(
        () => asTeamSet(match?.userId1, match?.userId1Partner),
        [match?.userId1, match?.userId1Partner]
    );
    const rightTeamSet = React.useMemo(
        () => asTeamSet(match?.userId2, match?.userId2Partner),
        [match?.userId2, match?.userId2Partner]
    );

    /** czy w danym meczu H2H "lewa" drużyna była pod participant1 (P1)? */
    const isLeftOnP1 = React.useCallback(
        (m) => {
            const t1 = asTeamSet(m.userId1, m.userId1Partner);
            const t2 = asTeamSet(m.userId2, m.userId2Partner);
            if (setsEqual(t1, leftTeamSet)) return true;
            if (setsEqual(t2, leftTeamSet)) return false;

            // fallback dla singla (lub gdy backend nie poda partnerów w którymś meczu)
            if (m.userId1 === match?.userId1) return true;
            if (m.userId2 === match?.userId1) return false;

            // ostatecznie: przyjmij, że lewa to participant1
            return true;
        },
        [leftTeamSet, match?.userId1]
    );

    /** sformatuj wynik pojedynczego meczu do kierunku nagłówka */
    const formatScoreRow = React.useCallback(
        (m) => {
                 if (m.walkover) {
                       const ws = getWinnerSide(m); // "P1" | "P2" | null
                       if (!ws) return "WO";
                       const leftWasP1 = isLeftOnP1(m);
                       const leftWon = (ws === "P1" && leftWasP1) || (ws === "P2" && !leftWasP1);
                       return `WO dla ${leftWon ? leftLabel : rightLabel}`;
                     }

            const sets = Array.isArray(m.setResults) ? m.setResults : [];
            if (sets.length === 0) return "-";

            const leftWasP1 = isLeftOnP1(m);

            // obróć sety tak, by "lewa" drużyna była po lewej stronie wyniku
            const oriented = sets.map((s) => {
                const a = Number(s.playerOneGames ?? 0);
                const b = Number(s.playerTwoGames ?? 0);
                return leftWasP1 ? [a, b] : [b, a];
            });

            // utnij końcowe 0:0 (np. 3. set przy 2:0)
            let end = oriented.length;
            for (let i = oriented.length - 1; i >= 0; i--) {
                const [x, y] = oriented[i];
                if (x === 0 && y === 0) end = i;
                else break;
            }
            const visible = oriented.slice(0, end);
            if (!visible.length) return "-";

            const score = visible.map(([x, y]) => `${x}:${y}`).join(" ");
            return m.injuryResignation ? `${score} Ret.` : score;
        },
        [isLeftOnP1, getWinnerSide, leftLabel, rightLabel]
    );

    /** zlicz H2H (A:B) zgodnie z nagłówkiem */
    const h2hTally = React.useMemo(() => {
        let leftWins = 0,
            rightWins = 0;
        (Array.isArray(h2hData) ? h2hData : []).forEach((m) => {
            const ws = getWinnerSide(m);
            if (!ws) return;
            const leftWasP1 = isLeftOnP1(m);
            const leftWon = (ws === "P1" && leftWasP1) || (ws === "P2" && !leftWasP1);
            if (leftWon) leftWins++;
            else rightWins++;
        });
        return { leftWins, rightWins };
    }, [h2hData, getWinnerSide, isLeftOnP1]);

    /** posortowane mecze malejąco po id (najnowsze na górze) */
    const sortedH2H = React.useMemo(() => {
        const arr = Array.isArray(h2hData) ? [...h2hData] : [];
        arr.sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));
        return arr;
    }, [h2hData]);

    /** komunikat w razie braku `match` przy otwartym modalu */
    React.useEffect(() => {
        if (isOpen && !isLoading && !match) {
            setModalMessage("Brak danych meczu do wyświetlenia H2H.");
            setIsInfoOpen(true);
        }
    }, [isOpen, isLoading, match]);

    if (!isOpen) return null;


    return (
        <div className={styles.modalOverlay}>
            {/* modal info (np. błąd) */}
            <CustomModal
                isOpen={isInfoOpen}
                onRequestClose={() => setIsInfoOpen(false)}
                message={modalMessage}
                cancelLabel="Zamknij"
                cancelButtonColor="green"
            />

            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.team}>
                        <div>{leftLabel}</div>
                        {Number.isFinite(match?.participant1EloGainIfWin) && (
                            <div
                                className={styles.eloGain}
                                title="Punkty ELO do zdobycia za wygranie tego meczu"
                            >
                                +{match.participant1EloGainIfWin} ELO
                            </div>
                        )}
                    </div>
                    <div className={styles.score}>{h2hTally.leftWins} : {h2hTally.rightWins}</div>
                    <div className={styles.teamRight}>
                        <div>{rightLabel}</div>
                        {Number.isFinite(match?.participant2EloGainIfWin) && (
                            <div
                                className={styles.eloGain}
                                title="Punkty ELO do zdobycia za wygranie tego meczu"
                            >
                                +{match.participant2EloGainIfWin} ELO
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.body}>
                    {isLoading ? (
                        <div className={styles.center}><Spinner /></div>
                    ) : sortedH2H.length === 0 ? (
                        <div className={styles.empty}>Brak wspólnych meczów.</div>
                    ) : (
                        <div className={styles.list}>
                            {sortedH2H.map((m) => (
                                <div key={m.id} className={styles.row}>
                                    <div className={styles.tournament}>{m.tournamentTitle}</div>
                                    <div className={styles.rowScore}>{formatScoreRow(m)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <button className={styles.closeBtn} onClick={onRequestClose}>Zamknij</button>
                </div>
            </div>
        </div>
    );
};

export default H2HModal;
